import React from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl-next';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { HOME_DRAFTS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { PageCards } from '@confluence/page-card/entry-points/PageCards.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';

import { untitled, HomeViewFilters } from '../homeViewsQueryHelpers.ts';
import { Options, useOptions } from '../Options/index.ts';
import { LoadingAndPagination } from '../LoadingAndPagination.tsx';
import { ExperienceTracking } from '../ExperienceTracking.tsx';
import { CreatePageButton } from '../CreatePageButton.tsx';
import DraftsEmptyStateLight from '../images/drafts/DraftsEmptyStateLight.svg';
import DraftsEmptyStateDark from '../images/drafts/DraftsEmptyStateDark.svg';
import { HOME_DRAFTS_VIEW_SEGMENT } from '../../../perf.config.ts';

import { useDraftsData } from './useDraftsData.ts';

const i18n = defineMessages({
	draftsTitle: {
		id: 'home.views.drafts.title',
		defaultMessage: 'Drafts',
		description: 'Header for the list of the users drafts',
	},
	noDraftsResults: {
		id: 'home.views.drafts.no-results.no-drafts',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We can't find any drafts that match.",
		description: 'Header shown when all drafts have been filtered out',
	},
	noDraftsSubtext: {
		id: 'home.views.drafts.no-results.no-drafts-subtext',
		defaultMessage: '<a>Search published pages instead</a>.',
		description: 'message suggesting navigation to advanced search',
	},
	emptyHeader: {
		id: 'home.views.drafts.empty.header',
		defaultMessage: 'You haven’t created any drafts yet',
		description:
			'Descriptive header that appears on the Drafts Home view when the user has no drafts.',
	},
	emptyBody: {
		id: 'home.views.drafts.empty.body',
		defaultMessage:
			'After you create a page, save your thoughts before publishing by selecting <strong>Close</strong> instead of <strong>Publish</strong>.',
		description:
			'Descriptive body text that appears on the Drafts Home view when the user has no drafts',
	},
});

const draftsContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const draftsHeaderStyles = css({
	font: token('font.heading.large'),
	marginBottom: token('space.200'),
});

const pageCardsContainerStyles = css({
	position: 'relative',
});

const draftsEmptyStateImage = {
	src: DraftsEmptyStateLight,
	srcDark: DraftsEmptyStateDark,
};

export const Drafts = () => {
	const intl = useIntl();
	const optionsProps = useOptions();

	const { pageCardData, loading, loadMore, hasNextPage, error } = useDraftsData({
		filterText: optionsProps.filterText,
		translatedUntitled: intl.formatMessage(untitled),
	});

	return (
		<UFOSegment name="all-drafts-page">
			<div css={draftsContainerStyles} data-testid="home_drafts">
				<PageSegmentLoadStart metric={HOME_DRAFTS_VIEW_SEGMENT} />
				<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.draftsTitle)} />
				{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
				<h1 css={draftsHeaderStyles}>
					<FormattedMessage {...i18n.draftsTitle} />
				</h1>
				<Options {...optionsProps} />
				<div css={pageCardsContainerStyles}>
					{!optionsProps.loading && (
						<PageCards
							appearance={optionsProps.appearance}
							nodes={pageCardData}
							analyticsData={{ source: 'draftsScreen' }}
						/>
					)}
					<LoadingAndPagination
						pageCardDataLength={pageCardData.length}
						loading={loading}
						hasNextPage={hasNextPage}
						loadMore={loadMore}
						error={error}
						noResultsMessage={i18n.noDraftsResults}
						noResultsSubtext={i18n.noDraftsSubtext}
						emptyMessageHeader={i18n.emptyHeader}
						emptyMessageBody={i18n.emptyBody}
						emptyMessageImage={draftsEmptyStateImage}
						emptyMessageCallToAction={
							<CreatePageButton
								appearance="primary"
								creationLinkSource="home-drafts"
								showCreateLivePageButton={false}
							/>
						}
						filterText={optionsProps.filterText}
						source="draftsScreen"
						filter={HomeViewFilters.DRAFTS}
					/>
				</div>
				<ExperienceTracking
					experienceName={HOME_DRAFTS_EXPERIENCE}
					loading={loading}
					error={error}
				/>
				{!loading && <PageSegmentLoadEnd metric={HOME_DRAFTS_VIEW_SEGMENT} />}
			</div>
		</UFOSegment>
	);
};
